import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Breadcrumbs,
  Link as MLink,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { red, orange } from "@mui/material/colors";
import {
  SchoolRounded,
  MenuBookRounded,
  VerifiedRounded,
  AccessTimeRounded,
  DownloadRounded,
} from "@mui/icons-material";
// icons
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import WorkRounded from "@mui/icons-material/WorkRounded";
import BusinessCenterRounded from "@mui/icons-material/BusinessCenterRounded";

import { Link as RouterLink, useParams } from "react-router-dom";
import Header from "./header";
import Footer from "./footer";
import { courseData } from "./data/courseData"; // <-- change the path to your data
import Seo from "./components/Seo";
// ---------- THEME HELPERS ----------
const ORANGE_ACCENT = orange?.A400 || orange[600] || "#ff9100";
const splitGrowthSteps = (s = "") =>
  s
    .split(/→|->|›|>/)
    .map((t) => t.trim())
    .filter(Boolean);

const careerAnchorId = (title = "") =>
  "career-" + title.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const headerLikeBg = {
  position: "relative",
  overflow: "hidden",
  backgroundImage: `linear-gradient(135deg, ${alpha(
    red[600],
    0.08
  )} 0%, ${alpha(ORANGE_ACCENT, 0.08)} 100%)`,
  border: (t) => `1px solid ${t.palette.divider}`,
  "&:before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(rgba(255,255,255,.16) 1px, transparent 1.5px)",
    backgroundSize: "14px 14px",
    pointerEvents: "none",
  },
};

// ---------- ADAPTER: use your existing courseData ----------
/**
 * Make sure you export your courseData from wherever it’s defined.
 * It should at least have: courseName (string), image (string), courseDesc (string[])
 */

const slugify = (s = "") =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Optional: try to infer stream from the name if not present
const inferStream = (name = "") => {
  const s = name.toLowerCase();
  if (/physics|chemistry|math|biology|science|computer/.test(s))
    return "Science";
  if (/account|business|commerce|economics/.test(s)) return "Commerce";
  if (/history|political|english|arts|sociology|psychology|geography/.test(s))
    return "Arts";
  return "General Studies";
};

// Defaults if details not provided
const defaultDetailsByStream = (stream = "General Studies") => {
  if (stream === "Science") {
    return {
      duration: "2 Years (Intermediate)",
      board: "JAC / CBSE-equivalent",
      highlights: [
        "Concept-first learning with labs & demos",
        "Weekly practice tests & numericals",
        "Olympiad & entrance mentorship",
      ],
      eligibility: [
        "Completed Class 10 from a recognized board",
        "Aptitude for mathematics and problem solving",
      ],
      syllabusPoints: [
        "Mechanics & Motion",
        "Electricity & Magnetism",
        "Optics & Waves",
        "Modern Concepts",
      ],
      careers: [
        {
          title: "Engineer (Core/Allied)",
          summary: "Solid foundation for engineering disciplines.",
          roles: ["Junior Engineer", "Design Engineer", "R&D Engineer"],
          growth: "Team Lead → Project Manager → Domain Specialist",
          sectors: ["Manufacturing", "Energy", "Aerospace", "Automotive"],
        },
        {
          title: "Research & Academia",
          summary: "Pathway via B.Sc. → M.Sc./Integrated programs.",
          roles: ["Research Intern", "Lab Assistant", "Lecturer"],
          growth: "Assistant Professor → Professor / Scientist",
          sectors: ["Universities", "National Labs", "R&D Institutes"],
        },
      ],
    };
  }
  if (stream === "Commerce") {
    return {
      duration: "2 Years (Intermediate)",
      board: "JAC / CBSE-equivalent",
      highlights: [
        "Practice-led learning with real caselets",
        "Basic Tally/ERP exposure (where available)",
        "Career prep for B.Com, CA/CS/CMA",
      ],
      eligibility: [
        "Completed Class 10 from a recognized board",
        "Interest in business, finance, and numbers",
      ],
      syllabusPoints: [
        "Accounting Fundamentals",
        "Company/Partnership Accounts",
        "GST Basics",
        "Business Law Basics",
      ],
      careers: [
        {
          title: "Accounting & Finance",
          summary: "Stepping stone to B.Com/CA/CMA/CS roles.",
          roles: ["Accounts Executive", "Audit Trainee", "Tax Associate"],
          growth: "Senior Accountant → Finance Manager → Controller",
          sectors: ["FMCG", "Consulting", "Banking", "SMEs"],
        },
        {
          title: "Banking & Insurance",
          summary: "Customer ops and back-office pathways.",
          roles: ["Customer Associate", "Back-office Executive", "Loan Ops"],
          growth: "Officer → Branch Manager → Regional Ops",
          sectors: ["Banks", "NBFCs", "Insurance"],
        },
      ],
    };
  }
  if (stream === "Arts") {
    return {
      duration: "2 Years (Intermediate)",
      board: "JAC / CBSE-equivalent",
      highlights: [
        "Reading circles & presentations",
        "Writing labs: essays, op-eds, reports",
        "Theatre & debate opportunities",
      ],
      eligibility: [
        "Completed Class 10 from a recognized board",
        "Interest in reading, writing, and communication",
      ],
      syllabusPoints: [
        "Prose & Poetry",
        "Communication & Composition",
        "Functional English",
        "Project/Portfolio",
      ],
      careers: [
        {
          title: "Media & Communications",
          summary: "Writing, content, PR, and media roles.",
          roles: ["Content Writer", "Social Media Exec", "Copy Assistant"],
          growth: "Senior Writer → Editor / Comms Lead",
          sectors: ["Media Houses", "Agencies", "Startups"],
        },
        {
          title: "Education & Training",
          summary: "Teaching/training with B.A./B.Ed. etc.",
          roles: ["Assistant Teacher", "Tutor", "Trainer"],
          growth: "Senior Teacher → Coordinator → Academic Head",
          sectors: ["Schools", "EdTech", "Coaching"],
        },
      ],
    };
  }
  return {
    duration: "2 Years (Intermediate)",
    board: "JAC / CBSE-equivalent",
    highlights: [
      "Balanced curriculum",
      "Mentor-led learning",
      "Skill labs & projects",
    ],
    eligibility: ["Completed Class 10 from a recognized board"],
    syllabusPoints: [
      "Core Concepts",
      "Applied Practice",
      "Projects",
      "Assessment",
    ],
    careers: [
      {
        title: "Higher Education",
        summary: "Progress to undergraduate programs.",
        roles: ["Student Intern", "Assistant"],
        growth: "Graduate → Postgraduate → Specialist",
        sectors: ["Universities", "Institutes"],
      },
    ],
  };
};

// OPTIONAL: precise details for specific slugs (fill in when ready)
const detailsBySlug = {
  // "science-physics": { duration: "...", board: "...", highlights: [...], eligibility: [...], syllabusPoints: [...], syllabusPDF: "/assets/syllabus/physics.pdf", careers: [...] }
};

// Merge your courseData with details/defaults into an index keyed by slug
const buildCoursesIndex = (cd = [], extra = {}) => {
  const out = {};
  cd.forEach((item) => {
    const name = item.courseName || item.name || "Course";
    const slug = slugify(name);
    const stream = item.stream || inferStream(name); // use existing field or infer
    const merged = {
      slug,
      name,
      stream,
      duration: item.duration, // can set in your courseData
      board: item.board,
      coverImage: item.image || item.image || "",
      summary:
        item.summary ||
        (Array.isArray(item.courseDesc)
          ? item.courseDesc.join(" · ")
          : "A comprehensive, mentor-led program."),
      highlights: item.highlights,
      eligibility: item.eligibility,
      syllabusPoints: item.syllabusPoints,
      syllabusPDF: item.syllabusPDF,
      careers: item.careers,
      // overlay precise/details or fall back to stream defaults
      ...defaultDetailsByStream(stream),
      ...(extra[slug] || {}),
    };
    // ensure coverImage/summary from courseData override defaults when present
    if (item.image) merged.coverImage = item.image;
    if (item.courseDesc)
      merged.summary = Array.isArray(item.courseDesc)
        ? item.courseDesc.join(" · ")
        : String(item.courseDesc);
    out[slug] = merged;
  });
  return out;
};

const COURSES = buildCoursesIndex(courseData, detailsBySlug);

// ---------- PAGE ----------
export default function CourseDetailsPage() {
  const { slug } = useParams();
  const course = COURSES[slug];

  const title = `${slug?.replace(/-/g, " ")} | Courses | AISECT Inter College`;
  const description = `View course details, eligibility, syllabus and updates for ${slug?.replace(
    /-/g,
    " "
  )} at AISECT Inter College.`;
  return (
    <>
    <Seo
        title={title}
        description={description}
        path={`/courses/${slug}`}
        image="https://aisectintercollege.in/assets/og/aisect_inter_college_bn_1.png"
      />
      <Header />

      {/* HERO */}
      <Box
        sx={{
          pt: { xs: 7, sm: 9 },
          pb: { xs: 5, sm: 7 },
          backgroundImage: `linear-gradient(135deg, ${alpha(
            red[600],
            0.1
          )} 0%, ${alpha(ORANGE_ACCENT, 0.1)} 100%)`,
          borderBottom: `1px solid ${alpha(red[600], 0.18)}`,
          position: "relative",
          overflow: "hidden",
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `radial-gradient(420px 260px at 0% 0%, ${alpha(
              "#fff",
              0.18
            )} 0%, transparent 60%),
               radial-gradient(460px 280px at 100% 100%, ${alpha(
                 "#fff",
                 0.1
               )} 0%, transparent 65%)`,
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ mb: 1, color: "text.secondary" }}
          >
            <MLink
              component={RouterLink}
              underline="hover"
              color="inherit"
              to="/"
            >
              Home
            </MLink>
            <MLink
              component={RouterLink}
              underline="hover"
              color="inherit"
              to="/courses"
            >
              Courses
            </MLink>
            <Typography color="text.primary">
              {course ? course.name : "Course"}
            </Typography>
          </Breadcrumbs>

          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: "clamp(28px, 4.8vw, 52px)",
              lineHeight: 1.12,
              letterSpacing: 0.2,
              background: `linear-gradient(90deg, ${red[700]} 0%, ${ORANGE_ACCENT} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {course ? course.name : "Course"}
          </Typography>

          {course && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
            >
              <Chip
                icon={<SchoolRounded />}
                label={course.stream}
                size="small"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                icon={<AccessTimeRounded />}
                label={course.duration}
                size="small"
                sx={{ fontWeight: 800 }}
              />
              <Chip
                icon={<VerifiedRounded />}
                label={course.board}
                size="small"
                sx={{ fontWeight: 800 }}
              />
            </Stack>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ my: { xs: 3.5, sm: 5 } }}>
        {!course ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>
              Course not found
            </Typography>
            <Button component={RouterLink} to="/courses" variant="contained">
              Back to Courses
            </Button>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2.5, sm: 3 }}>
            {/* LEFT */}
            <Grid item xs={12} md={8}>
              {/* Cover + Overview */}
              <Card elevation={0} sx={{ borderRadius: 3, ...headerLikeBg }}>
                {course.image && (
                  <Box
                    component="img"
                    src={course.image}
                    alt={`${course.name} cover`}
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: { xs: 260, sm: 320 },
                      objectFit: "cover",
                      borderRadius: 3,
                    }}
                  />
                )}
                <CardContent>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "clamp(18px, 2.6vw, 26px)",
                      mb: 1,
                    }}
                  >
                    Overview
                  </Typography>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "clamp(15.5px, 1.8vw, 18px)",
                      lineHeight: 1.85,
                    }}
                  >
                    {course.summary}
                  </Typography>
                </CardContent>
              </Card>

              {/* Highlights & Eligibility */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      p: { xs: 1.75, sm: 2 },
                      borderRadius: 3,
                      ...headerLikeBg,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <MenuBookRounded sx={{ color: red[600] }} />
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "clamp(16px, 2vw, 20px)",
                        }}
                      >
                        Key Highlights
                      </Typography>
                    </Stack>
                    <List dense disablePadding>
                      {(course.highlights || []).map((h, i) => (
                        <ListItem key={i} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 28, color: red[600] }}>
                            <VerifiedRounded fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={h}
                            primaryTypographyProps={{
                              sx: {
                                color: "text.secondary",
                                fontSize: "clamp(14px, 1.6vw, 16px)",
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    elevation={0}
                    sx={{
                      p: { xs: 1.75, sm: 2 },
                      borderRadius: 3,
                      ...headerLikeBg,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <VerifiedRounded sx={{ color: ORANGE_ACCENT }} />
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "clamp(16px, 2vw, 20px)",
                        }}
                      >
                        Eligibility
                      </Typography>
                    </Stack>
                    <List dense disablePadding>
                      {(course.eligibility || []).map((e, i) => (
                        <ListItem key={i} sx={{ px: 0 }}>
                          <ListItemIcon
                            sx={{ minWidth: 28, color: ORANGE_ACCENT }}
                          >
                            <SchoolRounded fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={e}
                            primaryTypographyProps={{
                              sx: {
                                color: "text.secondary",
                                fontSize: "clamp(14px, 1.6vw, 16px)",
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </Grid>
              </Grid>

              {/* Syllabus */}
              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  p: { xs: 1.75, sm: 2 },
                  borderRadius: 3,
                  ...headerLikeBg,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <MenuBookRounded sx={{ color: red[600] }} />
                  <Typography
                    sx={{ fontWeight: 800, fontSize: "clamp(16px, 2vw, 20px)" }}
                  >
                    Syllabus Snapshot
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {course.syllabusPDF && (
                    <Button
                      component="a"
                      href={course.syllabusPDF}
                      target="_blank"
                      rel="noreferrer"
                      size="small"
                      startIcon={<DownloadRounded />}
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                      Download PDF
                    </Button>
                  )}
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={1}>
                  {(course.syllabusPoints || []).map((p, i) => (
                    <Grid key={i} item xs={12} sm={6}>
                      <Typography
                        sx={{
                          color: "text.secondary",
                          fontSize: "clamp(14px, 1.6vw, 16px)",
                        }}
                      >
                        • {p}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Card>

              {/* Career Possibilities */}
              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  p: { xs: 1.75, sm: 2 },
                  borderRadius: 3,
                  ...headerLikeBg,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <AccountTreeRounded sx={{ color: ORANGE_ACCENT }} />
                  <Typography
                    sx={{ fontWeight: 800, fontSize: "clamp(16px, 2vw, 20px)" }}
                  >
                    Future Career Possibilities
                  </Typography>
                </Stack>

                <CareerTreeLite careers={course.careers || []} />

                <Typography
                  sx={{ mt: 1.25, color: "text.secondary", fontSize: 12 }}
                >
                  Note: Roles and growth depend on higher studies,
                  certifications, internships, skills, and market conditions.
                </Typography>
              </Card>
            </Grid>

            {/* RIGHT: Quick Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ position: "sticky", top: 88 }}>
                <Card
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.25 },
                    borderRadius: 3,
                    ...headerLikeBg,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: "clamp(18px, 2.2vw, 22px)",
                      mb: 1,
                    }}
                  >
                    Quick Info
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30, color: red[600] }}>
                        <SchoolRounded fontSize="medium" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Stream"
                        secondary={course.stream}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 800,
                            fontSize: 13.5,
                            color: "text.secondary",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontWeight: 800,
                            fontSize: "clamp(15.5px, 1.8vw, 18px)",
                          },
                        }}
                      />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30, color: red[600] }}>
                        <AccessTimeRounded fontSize="medium" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Duration"
                        secondary={course.duration}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 800,
                            fontSize: 13.5,
                            color: "text.secondary",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontWeight: 800,
                            fontSize: "clamp(15.5px, 1.8vw, 18px)",
                          },
                        }}
                      />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30, color: red[600] }}>
                        <VerifiedRounded fontSize="medium" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Board"
                        secondary={course.board}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: 800,
                            fontSize: 13.5,
                            color: "text.secondary",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            fontWeight: 800,
                            fontSize: "clamp(15.5px, 1.8vw, 18px)",
                          },
                        }}
                      />
                    </ListItem>
                  </List>

                  {/* <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      component={RouterLink}
                      to="/admission/apply"
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                      Apply Now
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        fontWeight: 800,
                        borderColor: red[600],
                        color: red[700],
                        "&:hover": {
                          borderColor: red[700],
                          bgcolor: alpha(red[600], 0.06),
                        },
                      }}
                    >
                      Enquire
                    </Button>
                  </Stack> */}
                </Card>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>

      <Footer />
    </>
  );
}

function CareerTreeLite({ careers = [] }) {
  const allIds = React.useMemo(
    () =>
      (Array.isArray(careers) ? careers : []).map((c, i) =>
        careerAnchorId(c?.title || `career-${i}`)
      ),
    [careers]
  );

  // expand all top-level nodes by default
  // const initial = new Set(
  //   careers.map((c, i) => careerAnchorId(c?.title || `career-${i}`))
  // );
  const [expanded, setExpanded] = React.useState(() => new Set(allIds));
  React.useEffect(() => {
    setExpanded(new Set(allIds));
  }, [allIds]);
  const hasCareers = Array.isArray(careers) && careers.length > 0;

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!hasCareers) {
    return (
      <Typography sx={{ color: "text.secondary" }}>
        Details coming soon.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 0.5 }}>
      {careers.map((c, i) => {
        const id = careerAnchorId(c?.title || `career-${i}`);
        return (
          <CareerNode
            key={id}
            id={id}
            depth={0}
            career={c}
            expanded={expanded.has(id)}
            onToggle={() => toggle(id)}
          />
        );
      })}
    </Box>
  );
}

function CareerNode({ id, career, depth, expanded, onToggle }) {
  const hasChildren =
    !!career?.growth ||
    (Array.isArray(career?.roles) && career.roles.length) ||
    (Array.isArray(career?.sectors) && career.sectors.length);

  return (
    <Box id={id} sx={{ position: "relative", mb: 1.5 }}>
      {/* Header plate */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 1.25,
          py: 1.1,
          borderRadius: 1.5,
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.error.light}22, ${t.palette.warning.main}22)`,
          border: (t) => `1px solid ${t.palette.error.light}55`,
        }}
      >
        <WorkRounded sx={{ mt: "2px", color: (t) => t.palette.error.main }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 900,
              lineHeight: 1.25,
              fontSize: { xs: 15.5, sm: 16.5, md: 18 },
            }}
          >
            {career?.title || "Career"}
          </Typography>
          {career?.summary && (
            <Typography
              sx={{
                color: "text.secondary",
                mt: 0.25,
                lineHeight: { xs: 1.5, md: 1.65 },
                fontSize: { xs: 13.25, sm: 14.25, md: 15.25 },
              }}
            >
              {career.summary}
            </Typography>
          )}
        </Box>

        {/* copy deep-link */}
        <Button
          onClick={() => {
            try {
              const url = `${window.location.pathname}#${id}`;
              navigator.clipboard?.writeText(url);
            } catch {}
          }}
          size="small"
          variant="outlined"
          startIcon={<LinkRounded />}
          sx={{
            mr: 0.5,
            borderRadius: 2,
            fontWeight: 800,
            borderColor: (t) => t.palette.error.main,
            color: (t) => t.palette.error.dark,
            "&:hover": {
              borderColor: (t) => t.palette.error.dark,
              bgcolor: (t) => t.palette.error.light + "22",
            },
          }}
        >
          Link
        </Button>

        {/* expand / collapse */}
        {hasChildren && (
          <Button
            onClick={onToggle}
            size="small"
            endIcon={expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        )}
      </Box>

      {/* Children group with dashed connector */}
      {hasChildren && expanded && (
        <Box
          sx={{
            position: "relative",
            ml: 3,
            mt: 1.1,
            pl: 2,
            borderLeft: (t) => `1px dashed ${t.palette.error.light}`,
          }}
        >
          {/* Growth */}
          {career.growth && (
            <Box
              sx={{
                p: 1.25,
                mb: 1.1,
                borderRadius: 1.5,
                bgcolor: (t) => t.palette.background.paper,
                border: (t) => `1px solid ${t.palette.error.light}55`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <TrendingUpRounded color="warning" />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 13.5, sm: 14.5, md: 16 },
                  }}
                >
                  Growth path
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {splitGrowthSteps(career.growth).map((step, idx, arr) => (
                  <React.Fragment key={idx}>
                    <Chip
                      size="small"
                      label={step}
                      sx={{
                        fontWeight: 800,
                        "& .MuiChip-label": {
                          fontSize: { xs: 11.5, sm: 12.5, md: 13.25 },
                          fontWeight: 800,
                        },
                      }}
                    />
                    {idx < arr.length - 1 && (
                      <Typography
                        sx={{
                          mx: 0.5,
                          color: "text.disabled",
                          fontSize: { xs: 12, sm: 13, md: 14 },
                        }}
                      >
                        →
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          {/* Roles */}
          {Array.isArray(career.roles) && career.roles.length > 0 && (
            <Box
              sx={{
                p: 1.25,
                mb: 1.1,
                borderRadius: 1.5,
                bgcolor: (t) => t.palette.background.paper,
                border: (t) => `1px solid ${t.palette.error.light}55`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <WorkRounded color="error" />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 13.5, sm: 14.5, md: 16 },
                  }}
                >
                  Typical roles
                </Typography>
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {career.roles.map((r, i2) => (
                  <Chip
                    key={i2}
                    label={r}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      "& .MuiChip-label": {
                        fontSize: { xs: 11.5, sm: 12.5, md: 13.25 },
                        fontWeight: 800,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Sectors */}
          {Array.isArray(career.sectors) && career.sectors.length > 0 && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: (t) => t.palette.background.paper,
                border: (t) => `1px solid ${t.palette.error.light}55`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <BusinessCenterRounded color="error" />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 13.5, sm: 14.5, md: 16 },
                  }}
                >
                  Sectors
                </Typography>
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {career.sectors.map((s, i3) => (
                  <Chip
                    key={i3}
                    label={s}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      "& .MuiChip-label": {
                        fontSize: { xs: 11.5, sm: 12.5, md: 13.25 },
                        fontWeight: 700,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
